import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import {
    landmarkResiduals,
    LandmarkResidual,
    measuredResidualsMm,
} from '~/domain/landmark/landmark';
import {
    deformableFrameTransform,
    rigidFrameTransform,
} from '~/domain/registration/frameTransform';
import { Grid } from '~/engine/data/common/Grid';
import { toDvfField } from '~/hooks/workflows/dvf/deformableRegistrationField';
import { landmarkPairListState } from '~/state/atoms/landmarkAtoms';
import { useRunDeformation } from './useRunDeformation';
import { useRunRigidRegistration } from './useRunRigidRegistration';
import { useRunScans } from './useRunScans';

export interface ResidualScale {
    readonly voxelMm?: number;
    readonly gridMm?: number;
}

export interface TransformResiduals {
    readonly residuals: readonly LandmarkResidual[];
    readonly measuredMm: readonly number[];
}

export interface RunLandmarkResiduals {
    // Landmark pairs mapped through the run's rigid registration.
    readonly rigid?: TransformResiduals;
    // Landmark pairs mapped through the run's deformable registration.
    readonly deformable?: TransformResiduals;
    readonly scale: ResidualScale;
}

function coarsestSpacingMm(grid: Grid | undefined): number | undefined {
    return grid ? Math.max(grid.resX, grid.resY, grid.resZ) : undefined;
}

// The target registration error of each landmark pair under both the run's rigid and its deformable transform.
// Each transform maps the point on the target scan into the planning scan, where its partner was placed.
export function useRunLandmarkResiduals(): RunLandmarkResiduals {
    const pairs = useRecoilValue(landmarkPairListState);
    const { planning, target } = useRunScans();
    const rigidRegistration = useRunRigidRegistration();
    const deformation = useRunDeformation();

    return useMemo(() => {
        const fixedToMoving =
            rigidRegistration && target && planning
                ? rigidRegistration.getTransformFromTo(
                      target.frameOfReferenceUid,
                      planning.frameOfReferenceUid
                  )
                : undefined;
        const frames =
            target && planning
                ? {
                      fixed: target.frameOfReferenceUid,
                      moving: planning.frameOfReferenceUid,
                  }
                : undefined;
        const rigidResiduals = fixedToMoving
            ? landmarkResiduals(
                  pairs,
                  rigidFrameTransform(fixedToMoving),
                  frames
              )
            : undefined;
        const deformableResiduals = deformation
            ? landmarkResiduals(
                  pairs,
                  deformableFrameTransform(deformation.transform),
                  frames
              )
            : undefined;
        const field = toDvfField(deformation?.registration);
        const voxelSpacings = [
            coarsestSpacingMm(target?.grid),
            coarsestSpacingMm(planning?.grid),
        ].filter((spacing): spacing is number => spacing !== undefined);
        return {
            rigid: rigidResiduals && {
                residuals: rigidResiduals,
                measuredMm: measuredResidualsMm(rigidResiduals),
            },
            deformable: deformableResiduals && {
                residuals: deformableResiduals,
                measuredMm: measuredResidualsMm(deformableResiduals),
            },
            scale: {
                voxelMm: voxelSpacings.length
                    ? Math.max(...voxelSpacings)
                    : undefined,
                gridMm: field ? Math.max(...field.spacing) : undefined,
            },
        };
    }, [pairs, planning, target, rigidRegistration, deformation]);
}
